import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { MESSAGES, CURRENT_USER, getParticipant, formatMessageTime, getDateLabel } from './communicationsData';

const ROLE_BADGE = {
  investor: { label: 'Investor', bg: 'bg-primary/8', text: 'text-primary' },
  sponsor: { label: 'Sponsor', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  admin: { label: 'Admin', bg: 'bg-purple-50', text: 'text-purple-700' },
};

function Avatar({ participant, size = 32 }) {
  return (
    <div
      className="flex-shrink-0 flex items-center justify-center rounded-full text-white font-semibold select-none"
      style={{ width: size, height: size, background: participant?.color || '#3b4c4c', fontSize: size * 0.34 }}
      title={participant?.name}
    >
      {participant?.initials}
    </div>
  );
}

function FileIcon({ type, size = 20 }) {
  const config = {
    pdf: { icon: 'FileText', color: 'text-red-600', bg: 'bg-red-50' },
    xlsx: { icon: 'FileSpreadsheet', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    docx: { icon: 'FileText', color: 'text-[#3b4c4c]', bg: 'bg-primary/8' },
  };
  const c = config?.[type] || config?.pdf;
  return (
    <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${c?.bg}`}>
      <Icon name={c?.icon} size={size} className={c?.color} />
    </div>
  );
}

function AttachmentCard({ attachment }) {
  return (
    <div className="mt-2 flex items-center gap-3 px-3 py-2.5 rounded-xl border border-border bg-muted max-w-sm">
      <FileIcon type={attachment?.type} />
      <div className="flex-1 min-w-0">
        <p className="font-caption text-sm font-medium text-foreground truncate">{attachment?.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="font-caption text-xs text-muted-foreground">{attachment?.size}</span>
          <span className="font-caption text-xs px-1.5 py-0.5 rounded bg-primary/8 text-primary font-medium">{attachment?.version}</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors" title="Preview">
          <Icon name="Eye" size={14} />
        </button>
        <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors" title="Download">
          <Icon name="Download" size={14} />
        </button>
      </div>
    </div>
  );
}

function DealRefCard({ dealRef }) {
  const stageColors = {
    'IC Review': 'bg-purple-50 text-purple-700',
    'Due Diligence': 'bg-amber-50 text-amber-700',
    'Execution': 'bg-primary/8 text-primary',
    'Screening': 'bg-muted text-muted-foreground',
    'Asset Management': 'bg-emerald-50 text-emerald-700',
  };
  return (
    <div className="mt-2 flex items-center gap-3 px-3 py-2 rounded-xl border border-primary/15 bg-primary/4 max-w-xs">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon name="Briefcase" size={15} color="var(--color-primary)" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-caption text-sm font-semibold text-foreground truncate">{dealRef?.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`font-caption text-xs px-1.5 py-0.5 rounded font-medium ${stageColors?.[dealRef?.stage] || 'bg-muted text-muted-foreground'}`}>{dealRef?.stage}</span>
          <span className="font-caption text-xs text-muted-foreground">{dealRef?.sector}</span>
        </div>
      </div>
      <Icon name="ExternalLink" size={13} color="var(--color-muted-foreground)" className="flex-shrink-0" />
    </div>
  );
}

function QuotedReply({ quoted }) {
  return (
    <div className="mb-2 pl-3 border-l-2 border-border bg-muted rounded-r-lg py-1.5 pr-2">
      <p className="font-caption text-xs font-semibold text-foreground mb-0.5">{quoted?.senderName}</p>
      <p className="font-caption text-xs text-muted-foreground line-clamp-2">{quoted?.preview}</p>
    </div>
  );
}

function ReactionBar({ reactions, onReact }) {
  const emojis = ['👍', '✅', '🔍'];
  return (
    <div className="flex items-center gap-1 mt-1.5">
      {emojis?.map(emoji => {
        const count = reactions?.[emoji] || 0;
        return count > 0 ? (
          <button
            key={emoji}
            onClick={() => onReact(emoji)}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted hover:bg-border text-xs transition-colors"
          >
            <span>{emoji}</span>
            <span className="font-caption text-foreground font-medium">{count}</span>
          </button>
        ) : null;
      })}
      <button
        onClick={() => {}}
        className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-6 h-6 rounded-full hover:bg-muted text-muted-foreground transition-all"
      >
        <Icon name="SmilePlus" size={13} />
      </button>
    </div>
  );
}

function MessageBubble({ message, isOwn }) {
  const sender = getParticipant(message?.senderId);
  const roleConfig = ROLE_BADGE?.[sender?.role] || ROLE_BADGE?.investor;
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      className="flex items-start gap-3 group py-1 px-2 rounded-xl hover:bg-muted transition-colors"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <Avatar participant={sender} size={32} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-caption text-sm font-semibold text-foreground">{sender?.name}</span>
          <span className={`font-caption text-xs px-1.5 py-0.5 rounded font-medium ${roleConfig?.bg} ${roleConfig?.text}`}>{roleConfig?.label}</span>
          <span className="font-caption text-xs text-muted-foreground">{formatMessageTime(message?.timestamp)}</span>
          {message?.edited && <span className="font-caption text-xs text-muted-foreground italic">(edited)</span>}
        </div>

        {message?.quotedReply && <QuotedReply quoted={message?.quotedReply} />}

        <p className="font-caption text-sm text-foreground leading-relaxed whitespace-pre-wrap">{message?.content}</p>

        {message?.attachment && <AttachmentCard attachment={message?.attachment} />}
        {message?.dealRef && <DealRefCard dealRef={message?.dealRef} />}

        <ReactionBar reactions={message?.reactions} onReact={() => {}} />

        {message?.replyCount > 0 && (
          <button className="mt-1 font-caption text-xs text-primary font-medium hover:underline flex items-center gap-1">
            <Icon name="MessageSquare" size={11} />
            {message?.replyCount} {message?.replyCount === 1 ? 'reply' : 'replies'}
          </button>
        )}
      </div>
      {/* Hover actions */}
      <div className={`flex items-center gap-0.5 transition-opacity ${showActions ? 'opacity-100' : 'opacity-0'}`}>
        <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="React">
          <Icon name="SmilePlus" size={14} />
        </button>
        <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Reply">
          <Icon name="Reply" size={14} />
        </button>
        <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="More">
          <Icon name="MoreHorizontal" size={14} />
        </button>
      </div>
    </div>
  );
}

function DateDivider({ label }) {
  return (
    <div className="flex items-center gap-3 my-3 px-2">
      <div className="flex-1 h-px bg-border" />
      <span className="font-caption text-xs font-medium text-muted-foreground px-2 py-0.5 rounded-full bg-muted">{label}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

function ConversationHeader({ thread, onToggleRightPanel, rightPanelOpen }) {
  const participants = thread?.participants?.slice(0, 4)?.map(id => getParticipant(id)) || [];
  const overflow = (thread?.participants?.length || 0) - 4;

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading text-sm font-semibold text-foreground truncate">{thread?.name}</h3>
          {thread?.dealLinked && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/8 text-primary cursor-pointer hover:bg-primary/15 transition-colors">
              <Icon name="Link2" size={10} />
              {thread?.dealLinked}
            </span>
          )}
        </div>
      </div>
      {/* Participant avatars */}
      <div className="flex items-center">
        {participants?.map((p, i) => (
          <div key={p?.id} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: participants?.length - i }}>
            <Avatar participant={p} size={28} />
          </div>
        ))}
        {overflow > 0 && (
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full bg-muted text-muted-foreground text-xs font-semibold border-2 border-card"
            style={{ marginLeft: -8 }}
          >
            +{overflow}
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex items-center gap-0.5">
        {[{ icon: 'Search', title: 'Search in thread' }, { icon: 'Pin', title: 'Pin thread' }, { icon: 'UserPlus', title: 'Add participants' }, { icon: 'BellOff', title: 'Mute' }]?.map(a => (
          <button key={a?.icon} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title={a?.title}>
            <Icon name={a?.icon} size={15} />
          </button>
        ))}
        <button
          onClick={onToggleRightPanel}
          className={`p-1.5 rounded-lg transition-colors ${
            rightPanelOpen ? 'bg-primary/8 text-primary' : 'hover:bg-muted text-muted-foreground hover:text-foreground'
          }`}
          title="Thread details"
        >
          <Icon name="PanelRight" size={15} />
        </button>
      </div>
    </div>
  );
}

function MessageComposer({ onSend, onAttach }) {
  const [text, setText] = useState('');
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const textareaRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      if (text?.trim()) handleSend();
    }
  };

  const handleSend = () => {
    if (!text?.trim()) return;
    onSend(text?.trim());
    setText('');
  };

  return (
    <div className="px-4 py-3 border-t border-border bg-card">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 mb-2">
        {[
          { icon: 'Bold', title: 'Bold', active: bold, toggle: () => setBold(b => !b) },
          { icon: 'Italic', title: 'Italic', active: italic, toggle: () => setItalic(i => !i) },
          { icon: 'List', title: 'Bullet list', active: false, toggle: () => {} },
          { icon: 'Link', title: 'Link', active: false, toggle: () => {} },
        ]?.map(t => (
          <button
            key={t?.icon}
            onClick={t?.toggle}
            className={`p-1.5 rounded transition-colors ${
              t?.active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            title={t?.title}
          >
            <Icon name={t?.icon} size={14} />
          </button>
        ))}
        <div className="w-px h-4 bg-border mx-1" />
        <button
          onClick={onAttach}
          className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Attach document"
        >
          <Icon name="Paperclip" size={14} />
        </button>
        <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Mention (@)">
          <Icon name="AtSign" size={14} />
        </button>
        <button className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Emoji">
          <Icon name="Smile" size={14} />
        </button>
      </div>
      {/* Input area */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e?.target?.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a message…"
          rows={2}
          maxLength={2000}
          className="w-full px-3 py-2.5 pr-24 font-caption text-sm bg-muted border border-border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 leading-relaxed"
          style={{ fontStyle: italic ? 'italic' : 'normal', fontWeight: bold ? '600' : 'normal' }}
        />
        <div className="absolute right-2 bottom-2 flex items-center gap-1.5">
          <span className="font-caption text-xs text-muted-foreground">{text?.length}/2000</span>
          <button
            onClick={handleSend}
            disabled={!text?.trim()}
            className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${
              text?.trim() ? 'bg-primary text-white hover:bg-primary/90 shadow-sm' : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
            title="Send (Enter)"
          >
            <Icon name="Send" size={14} />
          </button>
        </div>
      </div>
      <p className="font-caption text-xs text-muted-foreground mt-1">Press Enter to send · Shift+Enter for new line</p>
    </div>
  );
}

export default function CentrePanel({ thread, onToggleRightPanel, rightPanelOpen, onAttach }) {
  const feedRef = useRef(null);
  const [messages, setMessages] = useState(MESSAGES?.filter(m => m?.threadId === 'thread-1'));
  const [typing] = useState({ name: 'Sarah Chen', initials: 'SC', color: '#0f5132' });

  useEffect(() => {
    if (feedRef?.current) {
      feedRef.current.scrollTop = feedRef?.current?.scrollHeight;
    }
  }, [messages]);

  const handleSend = (text) => {
    const newMsg = {
      id: `msg-new-${Date.now()}`,
      threadId: thread?.id || 'thread-1',
      senderId: CURRENT_USER?.id,
      content: text,
      timestamp: new Date()?.toISOString(),
      edited: false,
      reactions: {},
      replyCount: 0,
      attachment: null,
      quotedReply: null,
    };
    setMessages(prev => [...prev, newMsg]);
  };

  if (!thread) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/8 flex items-center justify-center mx-auto mb-4">
            <Icon name="MessageSquare" size={32} color="var(--color-primary)" style={{ opacity: 0.4 }} />
          </div>
          <h3 className="font-heading text-base font-semibold text-foreground mb-1">Select a conversation to get started</h3>
          <p className="font-caption text-sm text-muted-foreground">Choose a thread from the left panel or compose a new message</p>
        </div>
      </div>
    );
  }

  // Group messages by date
  const groupedMessages = [];
  let lastDateLabel = null;
  messages?.forEach(msg => {
    const label = getDateLabel(msg?.timestamp);
    if (label !== lastDateLabel) {
      groupedMessages?.push({ type: 'divider', label });
      lastDateLabel = label;
    }
    groupedMessages?.push({ type: 'message', data: msg });
  });

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-card">
      <ConversationHeader thread={thread} onToggleRightPanel={onToggleRightPanel} rightPanelOpen={rightPanelOpen} />
      {/* Message feed */}
      <div ref={feedRef} className="flex-1 overflow-y-auto py-2">
        {messages?.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="font-caption text-sm text-muted-foreground">No messages yet. Start the conversation.</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {groupedMessages?.map((item, idx) =>
              item?.type === 'divider' ? (
                <DateDivider key={`div-${idx}`} label={item?.label} />
              ) : (
                <MessageBubble
                  key={item?.data?.id}
                  message={item?.data}
                  isOwn={item?.data?.senderId === CURRENT_USER?.id}
                />
              )
            )}
            {/* Typing indicator */}
            <div className="flex items-center gap-3 px-4 py-1">
              <div
                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-semibold"
                style={{ background: typing?.color }}
              >
                {typing?.initials}
              </div>
              <div className="flex items-center gap-1 px-3 py-2 rounded-xl bg-muted">
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--color-muted-foreground)', animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--color-muted-foreground)', animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--color-muted-foreground)', animationDelay: '300ms' }} />
              </div>
              <span className="font-caption text-xs text-muted-foreground">{typing?.name} is typing…</span>
            </div>
          </div>
        )}
      </div>
      <MessageComposer onSend={handleSend} onAttach={onAttach} />
    </div>
  );
}
